import {
	ArrowDownLeft,
	ArrowUpRight,
	ChevronDown,
	Pencil,
	Trash2,
} from "lucide-react";
import { cn } from "../../utilities/helpers";
import { Card } from "../card";
import { useCurrency } from "../dropdown";
import { formatCurrency, formatSignedCurrency, formatSignedPercent } from "../home/shared";

const formatDate = (value) => {
	if (!value) return "—";
	const date = value instanceof Date ? value : new Date(value);
	return Number.isNaN(date.getTime())
		? "—"
		: date.toLocaleDateString("en-GB", {
				day: "2-digit",
				month: "short",
				year: "numeric",
			});
};

const formatUnits = (units) =>
	Number(units || 0).toLocaleString("en-US", { maximumFractionDigits: 4 });

const formatSignedUnits = (units, isSell) =>
	`${isSell ? "−" : "+"}${formatUnits(Math.abs(Number(units || 0)))}`;

const TypeTag = ({ isSell }) => (
	<span
		className={cn(
			"inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium",
			isSell ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"
		)}
	>
		{isSell ? (
			<ArrowUpRight className="h-3.5 w-3.5" aria-hidden="true" />
		) : (
			<ArrowDownLeft className="h-3.5 w-3.5" aria-hidden="true" />
		)}
		{isSell ? "Sell" : "Buy"}
	</span>
);

const Stat = ({ label, value, valueClass, sub }) => (
	<div className="min-w-[92px] sm:text-right">
		<p className="text-xs text-slate-500">{label}</p>
		<p className={cn("text-sm font-semibold tabular-nums text-slate-900", valueClass)}>
			{value}
		</p>
		{sub && <p className="text-xs tabular-nums text-slate-500">{sub}</p>}
	</div>
);

const iconButtonClass =
	"inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 disabled:opacity-50";

const RowActions = ({
	row,
	label,
	isConfirming,
	isDeleting,
	onEdit,
	onRequestDelete,
	onCancelDelete,
	onConfirmDelete,
	align = "end",
}) => {
	if (isConfirming) {
		return (
			<div
				className={cn(
					"flex items-center gap-2",
					align === "end" ? "justify-end" : "justify-start"
				)}
			>
				<span className="text-xs text-slate-600">Delete?</span>
				<button
					type="button"
					onClick={onCancelDelete}
					className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
				>
					Cancel
				</button>
				<button
					type="button"
					autoFocus
					onClick={() => onConfirmDelete(row)}
					disabled={isDeleting}
					className="rounded-md bg-rose-600 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-rose-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-400 disabled:opacity-50"
				>
					{isDeleting ? "Deleting…" : "Delete"}
				</button>
			</div>
		);
	}

	return (
		<div
			className={cn(
				"flex items-center gap-1.5",
				align === "end" ? "justify-end" : "justify-start"
			)}
		>
			<button
				type="button"
				className={iconButtonClass}
				onClick={() => onEdit(row)}
				aria-label={`Edit ${label}`}
				title="Edit"
			>
				<Pencil className="h-4 w-4" aria-hidden="true" />
			</button>
			<button
				type="button"
				className={cn(iconButtonClass, "hover:bg-rose-50 hover:text-rose-700")}
				onClick={() => onRequestDelete(row)}
				aria-label={`Delete ${label}`}
				title="Delete"
			>
				<Trash2 className="h-4 w-4" aria-hidden="true" />
			</button>
		</div>
	);
};

const AssetTransactions = ({
	group,
	collapsed,
	onToggleCollapse,
	onEdit,
	onRequestDelete,
	onCancelDelete,
	onConfirmDelete,
	confirmingId,
	deletingId,
}) => {
	const { currency } = useCurrency();
	const isClosed = group.openUnits <= 0;
	const plValue = isClosed ? group.realizedPL : group.unrealizedPL;
	const plBasis = isClosed ? null : group.remainingCostBasis;
	const plPercent =
		plValue !== null && plBasis ? (plValue / plBasis) * 100 : null;
	const contentId = `asset-panel-${group.asset}`;

	const rowLabel = (row) =>
		`${row.transactionType === "SELL" ? "sell" : "buy"} of ${formatUnits(
			row.units
		)} ${group.label} on ${formatDate(row.effectiveDate)}`;

	return (
		<Card className="rounded-xl border-slate-200 bg-white shadow-sm">
			<div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6 md:px-5">
				<button
					type="button"
					onClick={onToggleCollapse}
					aria-expanded={!collapsed}
					aria-controls={contentId}
					className="-m-1 flex items-center gap-3 rounded-md p-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
				>
					<ChevronDown
						className={cn(
							"h-4 w-4 shrink-0 text-slate-400 transition-transform",
							collapsed && "-rotate-90"
						)}
						aria-hidden="true"
					/>
					<span className={cn("h-2.5 w-2.5 shrink-0 rounded-full", group.palette.dot)} />
					<span>
						<span className="flex items-center gap-2">
							<span className="text-base font-semibold text-slate-900">
								{group.label}
							</span>
							{isClosed && (
								<span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-600">
									Closed
								</span>
							)}
						</span>
						<span className="block text-xs text-slate-500">
							{group.rows.length} {group.rows.length === 1 ? "entry" : "entries"} ·{" "}
							{formatDate(group.firstDate)} – {formatDate(group.lastDate)}
						</span>
					</span>
				</button>

				<div className="grid grid-cols-2 gap-x-6 gap-y-3 pl-7 sm:flex sm:flex-wrap sm:items-center sm:gap-6 sm:pl-0">
					<Stat label="Units held" value={formatUnits(group.openUnits)} />
					<Stat
						label="Avg cost"
						value={
							group.averageCost !== null
								? formatCurrency(group.averageCost, currency)
								: "—"
						}
					/>
					<Stat
						label={isClosed ? "Net invested" : "Market value"}
						value={
							isClosed || group.currentWorth === null
								? formatCurrency(group.netInvested, currency)
								: formatCurrency(group.currentWorth, currency)
						}
					/>
					<Stat
						label={isClosed ? "Realised P/L" : "Unrealised P/L"}
						value={plValue !== null ? formatSignedCurrency(plValue, currency) : "—"}
						valueClass={
							plValue === null
								? undefined
								: plValue >= 0
									? "text-emerald-700"
									: "text-rose-700"
						}
						sub={plPercent !== null ? formatSignedPercent(plPercent) : undefined}
					/>
				</div>
			</div>

			{!collapsed && (
				<div id={contentId} className="border-t border-slate-100">
					{/* Desktop: a scannable table, scrolled inside its own box if narrow. */}
					<div className="hidden overflow-x-auto md:block">
						<table className="w-full min-w-[720px] text-sm">
							<caption className="sr-only">{group.label} transactions</caption>
							<thead>
								<tr className="border-b border-slate-100 text-xs text-slate-500">
									<th scope="col" className="px-5 py-2 text-left font-medium">
										Date
									</th>
									<th scope="col" className="px-3 py-2 text-left font-medium">
										Type
									</th>
									<th scope="col" className="px-3 py-2 text-right font-medium">
										Units
									</th>
									<th scope="col" className="px-3 py-2 text-right font-medium">
										Price
									</th>
									<th scope="col" className="px-3 py-2 text-right font-medium">
										Total
									</th>
									<th scope="col" className="px-3 py-2 text-right font-medium">
										Units after
									</th>
									<th scope="col" className="px-5 py-2 text-right font-medium">
										<span className="sr-only">Actions</span>
									</th>
								</tr>
							</thead>
							<tbody>
								{group.rows.map((row) => {
									const isSell = row.transactionType === "SELL";
									const unitPrice = Number(row.value || 0);
									const total = unitPrice * Number(row.units || 0);
									const isConfirming = confirmingId === row.id;

									return (
										<tr
											key={row.id}
											className={cn(
												"border-b border-slate-50 transition-colors last:border-b-0",
												isConfirming ? "bg-rose-50/60" : "hover:bg-slate-50"
											)}
										>
											<td className="whitespace-nowrap px-5 py-2.5 tabular-nums text-slate-900">
												{formatDate(row.effectiveDate)}
											</td>
											<td className="px-3 py-2.5">
												<TypeTag isSell={isSell} />
											</td>
											<td className="px-3 py-2.5 text-right tabular-nums text-slate-700">
												{formatSignedUnits(row.units, isSell)}
											</td>
											<td className="px-3 py-2.5 text-right tabular-nums text-slate-700">
												{formatCurrency(unitPrice, currency)}
												{row.sourceCurrency && row.sourceCurrency !== "USD" && (
													<span className="block text-xs text-slate-400">
														entered as {row.sourceCurrency}{" "}
														{Number(row.sourceValue || 0).toFixed(2)}
													</span>
												)}
											</td>
											<td className="px-3 py-2.5 text-right font-medium tabular-nums text-slate-900">
												{formatCurrency(total, currency)}
											</td>
											<td className="px-3 py-2.5 text-right tabular-nums text-slate-500">
												{formatUnits(row.holdingAfter)}
											</td>
											<td className="px-5 py-2.5">
												<RowActions
													row={row}
													label={rowLabel(row)}
													isConfirming={isConfirming}
													isDeleting={deletingId === row.id}
													onEdit={onEdit}
													onRequestDelete={onRequestDelete}
													onCancelDelete={onCancelDelete}
													onConfirmDelete={onConfirmDelete}
												/>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>

					{/* Mobile: stacked rows, so nothing has to scroll sideways. */}
					<ul className="divide-y divide-slate-100 md:hidden">
						{group.rows.map((row) => {
							const isSell = row.transactionType === "SELL";
							const unitPrice = Number(row.value || 0);
							const total = unitPrice * Number(row.units || 0);
							const isConfirming = confirmingId === row.id;

							return (
								<li
									key={row.id}
									className={cn(
										"flex items-start justify-between gap-3 px-4 py-3",
										isConfirming && "bg-rose-50/60"
									)}
								>
									<div className="min-w-0">
										<div className="flex flex-wrap items-center gap-2">
											<TypeTag isSell={isSell} />
											<span className="text-sm tabular-nums text-slate-900">
												{formatDate(row.effectiveDate)}
											</span>
										</div>
										<p className="mt-1 text-xs tabular-nums text-slate-500">
											{formatSignedUnits(row.units, isSell)} units @{" "}
											{formatCurrency(unitPrice, currency)}
										</p>
										<p className="text-xs tabular-nums text-slate-500">
											{formatUnits(row.holdingAfter)} units after
											{row.sourceCurrency && row.sourceCurrency !== "USD"
												? ` · entered as ${row.sourceCurrency} ${Number(
														row.sourceValue || 0
													).toFixed(2)}`
												: ""}
										</p>
									</div>
									<div className="flex shrink-0 flex-col items-end gap-2">
										<p className="text-sm font-semibold tabular-nums text-slate-900">
											{formatCurrency(total, currency)}
										</p>
										<RowActions
											row={row}
											label={rowLabel(row)}
											isConfirming={isConfirming}
											isDeleting={deletingId === row.id}
											onEdit={onEdit}
											onRequestDelete={onRequestDelete}
											onCancelDelete={onCancelDelete}
											onConfirmDelete={onConfirmDelete}
										/>
									</div>
								</li>
							);
						})}
					</ul>
				</div>
			)}
		</Card>
	);
};

export default AssetTransactions;
